import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Paper,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  Folder as FolderIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
// OCR için ChatGPT Vision API kullanacağız

interface ChatGptPrompt {
  _id: string;
  name: string;
  prompt: string;
  image?: string;
  status: 'active' | 'inactive';
  createdAt: string;
}

interface PromptGroup {
  name: string;
  count: number;
  prompts: ChatGptPrompt[];
  hasActive: boolean;
  hasInactive: boolean;
}

const ChatGptPrompts: React.FC = () => {
  const { user } = useAuth();
  const [prompts, setPrompts] = useState<ChatGptPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<ChatGptPrompt | null>(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [formData, setFormData] = useState({
    name: '',
    prompt: '',
    image: '',
    status: 'active' as 'active' | 'inactive',
  });
  const [ocrLoading, setOcrLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [improveLoading, setImproveLoading] = useState(false);
  
  // Klasörleme için yeni state'ler
  const [viewMode, setViewMode] = useState<'folders' | 'prompts'>('folders');
  const [selectedFolder, setSelectedFolder] = useState<string>('');
  const [promptGroups, setPromptGroups] = useState<PromptGroup[]>([]);

  // Check if user has connected accounts
  const noConnectedAccounts = !user?.facebookPageId && !user?.instagramBusinessId && !user?.whatsappBusinessAccountId && !user?.tiktokId;

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/chatgpt-prompts');
      setPrompts(response.data);
      
      // Prompt'ları grupla
      const groups = groupPromptsByName(response.data);
      setPromptGroups(groups);
    } catch (err) {
      setError('Failed to load Agent prompts');
    } finally {
      setLoading(false);
    }
  };

  // Prompt'ları isme göre grupla
  const groupPromptsByName = (prompts: ChatGptPrompt[]): PromptGroup[] => {
    const groups: { [key: string]: PromptGroup } = {};
    
    prompts.forEach(prompt => {
      if (!groups[prompt.name]) {
        groups[prompt.name] = {
          name: prompt.name,
          count: 0,
          prompts: [],
          hasActive: false,
          hasInactive: false,
        };
      }
      
      groups[prompt.name].count++;
      groups[prompt.name].prompts.push(prompt);
      
      if (prompt.status === 'active') {
        groups[prompt.name].hasActive = true;
      } else {
        groups[prompt.name].hasInactive = true;
      }
    });
    
    return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
  };

  useEffect(() => {
    // Only fetch prompts if user has connected accounts
    if (!noConnectedAccounts) {
      fetchPrompts();
    }
  }, [noConnectedAccounts]);

  const handleOpenDialog = (prompt?: ChatGptPrompt) => {
    if (prompt) {
      setEditingPrompt(prompt);
      setFormData({
        name: prompt.name || '',
        prompt: prompt.prompt,
        image: prompt.image || '',
        status: prompt.status || 'active',
      });
    } else {
      setEditingPrompt(null);
      setFormData({
        name: '',
        prompt: '',
        image: '',
        status: 'active',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPrompt(null);
    setFormData({
      name: '',
      prompt: '',
      image: '',
      status: 'active',
    });
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageData = e.target?.result as string;
        setFormData({ ...formData, image: imageData });
        
        // ChatGPT Vision API ile OCR işlemi
        setOcrLoading(true);
        try {
          const response = await axios.post('/api/extract-text-from-image', {
            image: imageData
          });
          
          const extractedText = response.data.text;
          if (extractedText) {
            setFormData(prev => ({
              ...prev,
              prompt: prev.prompt + (prev.prompt ? '\n\n' : '') + extractedText
            }));
          }
        } catch (error) {
          console.error('OCR error:', error);
        } finally {
          setOcrLoading(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImprovePrompt = async () => {
    if (!formData.prompt.trim()) {
      setError('Please enter a prompt first');
      return;
    }

    setImproveLoading(true);
    setError('');
    
    try {
      const response = await axios.post('/api/improve-prompt', {
        prompt: formData.prompt
      });

      if (response.data.success) {
        // Set the improved prompt to the form
        setFormData({ ...formData, prompt: response.data.improvedPrompt });
        
        // Success message (optional)
        console.log('✅ Prompt improved by AI');
      }
    } catch (err: any) {
      console.error('Prompt improvement error:', err);
      setError(err.response?.data?.error || 'An error occurred while improving the prompt');
    } finally {
      setImproveLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitLoading(true);
    try {
      if (editingPrompt) {
        await axios.put(`/api/chatgpt-prompts/${editingPrompt._id}`, formData);
      } else {
        await axios.post('/api/chatgpt-prompts', formData);
      }
      fetchPrompts();
      handleCloseDialog();
    } catch (err) {
      setError('Failed to save Agent prompt');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this Agent prompt?')) {
      try {
        await axios.delete(`/api/chatgpt-prompts/${id}`);
        fetchPrompts();
      } catch (err) {
        setError('Failed to delete Agent prompt');
      }
    }
  };

  const handleFolderClick = (folderName: string) => {
    setSelectedFolder(folderName);
    setViewMode('prompts');
  };

  const handleBackToFolders = () => {
    setViewMode('folders');
    setSelectedFolder('');
  };

  // Bulk status change
  const handleBulkStatusChange = async (status: 'active' | 'inactive') => {
    const folderPrompts = promptGroups.find(g => g.name === selectedFolder)?.prompts || [];
    const promptIds = folderPrompts.map(p => p._id);
    
    if (window.confirm(`Are you sure you want to ${status === 'active' ? 'activate' : 'deactivate'} all ${folderPrompts.length} prompts in this folder?`)) {
      try {
        // Her prompt için status'u güncelle
        const updatePromises = promptIds.map(id => 
          axios.patch(`/api/chatgpt-prompts/${id}/toggle-status`)
        );
        
        await Promise.all(updatePromises);
        fetchPrompts(); // Listeyi yenile
      } catch (err) {
        setError(`Failed to ${status === 'active' ? 'activate' : 'deactivate'} prompts`);
      }
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    const folderPrompts = promptGroups.find(g => g.name === selectedFolder)?.prompts || [];
    
    if (window.confirm(`Are you sure you want to delete all ${folderPrompts.length} prompts in this folder? This action cannot be undone!`)) {
      try {
        // Her prompt'u sil
        const deletePromises = folderPrompts.map(p => 
          axios.delete(`/api/chatgpt-prompts/${p._id}`)
        );
        
        await Promise.all(deletePromises);
        fetchPrompts(); // Listeyi yenile
        handleBackToFolders(); // Klasör görünümüne geri dön
      } catch (err) {
        setError('Failed to delete prompts');
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 150,
      renderCell: (params) => (
        <Box
          sx={{
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <Button
            size="small"
            variant={params.value === 'active' ? 'contained' : 'outlined'}
            color={params.value === 'active' ? 'success' : 'inherit'}
            onClick={async () => {
              try {
                await axios.patch(`/api/chatgpt-prompts/${params.row._id}/toggle-status`);
                fetchPrompts();
              } catch (err) {
                setError('Failed to toggle prompt status');
              }
            }}
            sx={{ 
              minWidth: '80px',
              textTransform: 'none',
              fontSize: '0.75rem'
            }}
          >
            {params.value === 'active' ? 'Active' : 'Inactive'}
          </Button>
        </Box>
      ),
    },
    {
      field: 'prompt',
      headerName: 'Prompt',
      flex: 1,
      minWidth: 300,
      renderCell: (params) => (
        <Box
          sx={{
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: 'image',
      headerName: 'Image',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          {params.value ? (
            <img 
              src={params.value} 
              alt="Prompt Image" 
              style={{ 
                width: '40px', 
                height: '40px', 
                objectFit: 'cover',
                borderRadius: '4px',
                border: '1px solid #e0e0e0',
                cursor: 'pointer',
                transition: 'transform 0.2s ease'
              }} 
              onClick={() => {
                setSelectedImage(params.value);
                setImageModalOpen(true);
              }}
              onMouseOver={(e) => (e.target as HTMLImageElement).style.transform = 'scale(1.1)'}
              onMouseOut={(e) => (e.target as HTMLImageElement).style.transform = 'scale(1)'}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              No Image
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleOpenDialog(params.row)}
            color="primary"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row._id)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  // Short helper descriptions for common folders
  const getFolderDescription = (name: string): string => {
    const key = name.trim().toLowerCase();
    switch (key) {
      case 'about-us':
      case 'about us':
        return 'A concise, warm overview of your brand: who you are, what you do, key value and trust signals. Keep it friendly and on‑brand.';
      case 'shipping':
        return 'Explain shipping times, regions, carriers, fees and tracking. Be clear and reassuring about delivery expectations.';
      case 'returns':
      case 'refunds':
        return 'Outline return window, conditions, simple steps and refund timing. Reduce friction and set clear expectations.';
      case 'pricing':
      case 'plans':
        return 'Summarize available plans, what’s included and how to upgrade/downgrade. Keep it straight and helpful.';
      case 'product':
      case 'products':
        return 'Highlight key benefits, who it’s for and essential specs. Focus on outcomes customers care about.';
      case 'support':
      case 'help':
        return 'Guide users to help options, response times and escalation. Stay calm, empathetic and solution‑oriented.';
      case 'contact':
        return 'Share how to reach you and typical reply times. Encourage contacting you for unresolved issues.';
      case 'faq':
        return 'Short, direct answers to the most common questions. Anticipate follow‑ups and link to details when needed.';
      case 'onboarding':
        return 'Step‑by‑step guidance for first‑time users with best practices to get value fast.';
      case 'promotions':
      case 'coupons':
        return 'Explain how promos/coupons work, eligibility and key limitations in clear terms.';
      default:
        return `${name}: Keep answers consistent, helpful and on‑brand. Anticipate common follow‑ups.`;
    }
  };

  // Klasör görünümü
  const renderFoldersView = () => (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              {/* <Typography variant="h5" gutterBottom>
                Agent Prompts
              </Typography> */}
              <Typography variant="body1" color="text.secondary">
                Manage AI prompts organized by name. Click on a folder to view individual prompts.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Prompt
            </Button>
          </Box>

          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)'
            },
            gap: 2 
          }}>
            {promptGroups.map((group) => (
              <Card 
                key={group.name}
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3,
                  }
                }}
                onClick={() => handleFolderClick(group.name)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" noWrap>
                      {group.name}
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {getFolderDescription(group.name)}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {group.count} prompt{group.count !== 1 ? 's' : ''}
                    </Typography>
                    <ExpandMoreIcon color="action" />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {group.hasActive && (
                      <Chip 
                        label="Active" 
                        size="small" 
                        color="success" 
                        variant="outlined"
                      />
                    )}
                    {group.hasInactive && (
                      <Chip 
                        label="Inactive" 
                        size="small" 
                        color="default" 
                        variant="outlined"
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </>
      )}
    </Box>
  );

  // Prompt'lar görünümü
  const renderPromptsView = () => {
    const folderPrompts = promptGroups.find(g => g.name === selectedFolder)?.prompts || [];
    return (
      <Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Link
                  component="button"
                  variant="body1"
                  onClick={handleBackToFolders}
                  sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                >
                  <ArrowBackIcon sx={{ mr: 0.5 }} />
                  Back to Folders
                </Link>
                <Typography color="text.primary">{selectedFolder}</Typography>
              </Breadcrumbs>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h5" gutterBottom>
                  {selectedFolder}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {folderPrompts.length} prompt{folderPrompts.length !== 1 ? 's' : ''} in this folder
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Add Prompt
              </Button>
            </Box>
            {/* Bulk Actions */}
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                Bulk Actions
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  color="success"
                  size="small"
                  onClick={() => handleBulkStatusChange('active')}
                  disabled={folderPrompts.every(p => p.status === 'active')}
                  sx={{ textTransform: 'none' }}
                >
                  Activate All
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={() => handleBulkStatusChange('inactive')}
                  disabled={folderPrompts.every(p => p.status === 'inactive')}
                  sx={{ textTransform: 'none' }}
                >
                  Deactivate All
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleBulkDelete}
                  sx={{ textTransform: 'none' }}
                >
                  Delete All
                </Button>
              </Box>
            </Box>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ height: 600, width: '100%' }}>
                <DataGrid
                  rows={folderPrompts}
                  columns={columns}
                  loading={loading}
                  disableRowSelectionOnClick
                  getRowId={(row) => row._id}
                  sx={{
                    '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid #e0e0e0',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: 'grey.50',
                      borderBottom: '2px solid #e0e0e0',
                    },
                  }}
                />
              </Box>
            </Paper>
          </>
        )}
      </Box>
    );
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  // If user has no connected accounts, show disabled header and centered overlay
  if (noConnectedAccounts) {
    return (
      <Box sx={{ position: 'relative', minHeight: 600 }}>
        {/* Normal header content */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="body1" color="text.secondary">
              Manage AI prompts organized by name. Connect an account to enable Agent responses.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled
            sx={{
              '&.Mui-disabled': {
                backgroundColor: 'rgba(0,0,0,0.08)',
                color: 'rgba(0,0,0,0.30)'
              }
            }}
          >
            Add Prompt
          </Button>
        </Box>

        {/* No grid here for Agent Prompts. We only show the overlay while keeping header visible */}

        {/* Centered overlay with warning and Connect button */}
        <Box sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1300,
          pointerEvents: 'auto',
          userSelect: 'auto',
          width: '100%',
          maxWidth: 520
        }}>
          <Alert severity="warning" sx={{
            backgroundColor: '#FFF7E6',
            border: '1px solid #FFE1A3',
            color: '#8A6D3B',
            width: '100%',
            textAlign: 'center',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
              No Connected Accounts
            </Typography>
            <Typography sx={{ mb: 3 }}>
              Connect at least one social media account to create and manage Agent responses.
            </Typography>
            <Button
              variant="contained"
              color="warning"
              onClick={() => window.dispatchEvent(new Event('open-connect-dropdown'))}
              sx={{ fontWeight: 600 }}
            >
              Connect Account
            </Button>
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {viewMode === 'folders' ? renderFoldersView() : renderPromptsView()}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPrompt ? 'Edit Agent Prompt' : 'Add Agent Prompt'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            helperText="Give your prompt a descriptive name"
          />
          
          <TextField
            fullWidth
            label="Prompt"
            value={formData.prompt}
            onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
            margin="normal"
            multiline
            rows={6}
            helperText="The Agent prompt template. Use {message} to include the user's message."
          />
          
          {/* AI ile Düzelt Butonu */}
          <Box sx={{ mt: 1, mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleImprovePrompt}
              disabled={!formData.prompt.trim() || improveLoading || submitLoading}
              startIcon={improveLoading ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
              sx={{ 
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'white'
                }
              }}
            >
              {improveLoading ? 'AI Improving...' : 'Improve with AI'}
            </Button>
          </Box>
          
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Status
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={formData.status === 'active' ? 'contained' : 'outlined'}
                color="success"
                size="small"
                onClick={() => setFormData({ ...formData, status: 'active' })}
                sx={{ textTransform: 'none' }}
              >
                Active
              </Button>
              <Button
                variant={formData.status === 'inactive' ? 'contained' : 'outlined'}
                color="inherit"
                size="small"
                onClick={() => setFormData({ ...formData, status: 'inactive' })}
                sx={{ textTransform: 'none' }}
              >
                Inactive
              </Button>
            </Box>
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Image (Optional) - Text will be extracted automatically
            </Typography>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="image-upload"
              type="file"
              onChange={handleImageUpload}
            />
            <label htmlFor="image-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={ocrLoading ? <CircularProgress size={20} /> : <ImageIcon />}
                disabled={ocrLoading}
                sx={{ mb: 2 }}
              >
                {ocrLoading ? 'Processing Image...' : 'Upload Image'}
              </Button>
            </label>
            {formData.image && (
              <Box sx={{ mt: 2 }}>
                <img 
                  src={formData.image} 
                  alt="Preview" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '200px', 
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                  }} 
                />
                <Button 
                  size="small" 
                  color="error" 
                  onClick={() => setFormData({ ...formData, image: '' })}
                  sx={{ mt: 1 }}
                >
                  Remove Image
                </Button>
              </Box>
            )}
          </Box>

        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitLoading}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={submitLoading}
            startIcon={submitLoading ? <CircularProgress size={20} /> : null}
          >
            {submitLoading ? 'Saving...' : (editingPrompt ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Modal */}
      <Dialog 
        open={imageModalOpen} 
        onClose={() => setImageModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Image Preview</Typography>
          <IconButton onClick={() => setImageModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <img 
              src={selectedImage} 
              alt="Full size preview" 
              style={{ 
                maxWidth: '100%', 
                maxHeight: '70vh',
                objectFit: 'contain'
              }} 
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ChatGptPrompts; 